<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import Button from 'primevue/button'
import AppNoticeBanner from '@/components/common/AppNoticeBanner.vue'
import { fetchPlatformIdentity, getBuildSha } from '@/version'

const props = withDefaults(
  defineProps<{
    reloadPage?: () => void
  }>(),
  {
    reloadPage: () => window.location.reload(),
  },
)

const updateAvailable = ref(false)
const POLL_INTERVAL_MS = 5 * 60 * 1000

let intervalId: ReturnType<typeof setInterval> | null = null

async function checkForUpdate(): Promise<void> {
  const { sha } = await fetchPlatformIdentity()
  if (sha && sha !== getBuildSha()) {
    updateAvailable.value = true
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
  }
}

function reload(): void {
  props.reloadPage()
}

onMounted(() => {
  const currentSha = getBuildSha()
  if (!currentSha) return
  intervalId = setInterval(checkForUpdate, POLL_INTERVAL_MS)
})

onUnmounted(() => {
  if (intervalId !== null) {
    clearInterval(intervalId)
    intervalId = null
  }
})
</script>

<template>
  <Transition name="version-slide">
    <AppNoticeBanner v-if="updateAvailable" class="version-banner" tone="info">
      A newer version of Moria is available. You can continue working, but refresh after saving your
      work to load the latest updates.
      <template #actions>
        <Button
          class="version-banner__refresh"
          label="Refresh"
          severity="secondary"
          outlined
          size="small"
          aria-label="Refresh the page to apply the update"
          @click="reload"
        />
      </template>
    </AppNoticeBanner>
  </Transition>
</template>

<style scoped>
.version-banner {
  position: fixed;
  top: var(--space-5);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-toast);
  max-width: calc(100vw - 2rem);
  margin-bottom: 0;
}

.version-slide-enter-active,
.version-slide-leave-active {
  transition:
    opacity 200ms var(--ease-default),
    transform 200ms var(--ease-default);
}
.version-slide-enter-from,
.version-slide-leave-to {
  opacity: 0;
  transform: translate(-50%, calc(-1 * var(--space-3)));
}

@media (prefers-reduced-motion: reduce) {
  .version-slide-enter-active,
  .version-slide-leave-active {
    transition: none;
  }
}
</style>
